import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CHARACTER_EMOTICON_TAB } from './emoticonTypes';
import { CharacterEmoticonData, type CharacterEmoticonCategory } from './CharacterEmoticonData';

interface CharacterEmoticonProps {
  callComponent: string;
  onSend: (code: string) => void;
}

const RECENT_KEY = 'wetalk_character_emoji';

const CharacterEmoticon: React.FC<CharacterEmoticonProps> = ({ callComponent, onSend }) => {
  const [activeTab, setActiveTab] = useState<CHARACTER_EMOTICON_TAB>(CHARACTER_EMOTICON_TAB.WEDAERI);
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  const [marginLeft, setMarginLeft] = useState(0);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 로컬 스토리지에서 최근 사용 내역 로드
  useEffect(() => {
    const saved = localStorage.getItem(RECENT_KEY);
    if (saved) {
      try {
        setRecentCodes(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse recent characters', e);
      }
    }
  }, []);

  const handleRecentlyUsed = (code: string) => {
    let newRecent = [code, ...recentCodes.filter(c => c !== code)];
    if (newRecent.length > 8) newRecent = newRecent.slice(0, 8);
    setRecentCodes(newRecent);
    localStorage.setItem(RECENT_KEY, JSON.stringify(newRecent));
  };

  const handleSelect = (code: string) => {
    onSend(code);
    handleRecentlyUsed(code);
  };

  const handleTabClick = (tab: CHARACTER_EMOTICON_TAB, categoryId: string) => {
    setActiveTab(tab);
    const target = document.getElementById(`${callComponent}_${categoryId}`);
    if (target && scrollWrapperRef.current) {
      const top = target.offsetTop - 56;
      scrollWrapperRef.current.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handlePrevNext = (direction: 'PREV' | 'NEXT') => {
    const step = 50;
    if (direction === 'PREV') {
      setMarginLeft(prev => Math.min(0, prev + step));
    } else {
      // 대략적인 한계점 (아이템 개수에 따라 달라지겠지만 원본 로직 모방)
      setMarginLeft(prev => Math.max(-100, prev - step));
    }
  };

  const dataWithRecent = recentCodes.length > 0 
    ? [
        {
          id: 'co.emoji.recent',
          title: '최근',
          categoryId: 'character_category_recent',
          items: recentCodes.map(code => ({ name: `charemo_inner ${code}`, code }))
        } as CharacterEmoticonCategory,
        ...CharacterEmoticonData
      ]
    : CharacterEmoticonData;

  return (
    <div>
      <div className="charemo_header">
        <div className="listwrap">
          <div 
            id={`${callComponent}_character_emoticon_list`} 
            className="listscroll" 
            style={{ marginLeft: `${marginLeft}px`, transition: 'margin-left 0.2s' }}
          >
            {recentCodes.length > 0 && (
              <button
                type="button"
                className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.RECENT ? 'selected' : ''}`}
                title="최근"
                onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.RECENT, 'character_category_recent')}
              >
                <span className="charemo_headitem dz_head000" />
              </button>
            )}
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.WEDAERI ? 'selected' : ''}`}
              title="위대리"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.WEDAERI, 'character_category_wedaeri')}
            >
              <span className="charemo_headitem dz_head001" />
            </button>
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.GOGWAJANG ? 'selected' : ''}`}
              title="고과장"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.GOGWAJANG, 'character_category_gogwajang')}
            >
              <span className="charemo_headitem dz_head002" />
            </button>
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.HASAWON ? 'selected' : ''}`}
              title="하사원"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.HASAWON, 'character_category_hasawon')}
            >
              <span className="charemo_headitem dz_head010" />
            </button>
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.DAHAGI ? 'selected' : ''}`}
              title="다하쥐"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.DAHAGI, 'character_category_dahagi')}
            >
              <span className="charemo_headitem dz_head011" />
            </button>
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.TONGCA ? 'selected' : ''}`}
              title="퉁카"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.TONGCA, 'character_category_tongca')}
            >
              <span className="charemo_headitem dz_head012" />
            </button>
            <button
              type="button"
              className={`charemo_btn ${activeTab === CHARACTER_EMOTICON_TAB.ETC ? 'selected' : ''}`}
              title="기타"
              onClick={() => handleTabClick(CHARACTER_EMOTICON_TAB.ETC, 'character_category_etc')}
            >
              <span className="charemo_headitem dz_head013" />
            </button>
          </div>
        </div>
        <button type="button" className="pagebtn prev" onClick={() => handlePrevNext('PREV')}>
          <span className="blind">이전</span>
        </button>
        <button type="button" className="pagebtn next" onClick={() => handlePrevNext('NEXT')}>
          <span className="blind">다음</span>
        </button>
      </div>
      <div 
        className="charemo_scroll_wrapper" 
        id={`${callComponent}_charemo_scroll_wrapper`}
        ref={scrollWrapperRef}
        style={{ overflowY: 'auto' }}
      >
        {dataWithRecent.map((category) => (
          <div className="charemo_section" key={category.id}>
            <h3 id={`${callComponent}_${category.categoryId}`}>
              {category.title}
            </h3>
            <div className="charemo_list">
              {category.items.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  className="charemo_btn"
                  onClick={() => handleSelect(item.code)}
                >
                  <span className="charemo_outer">
                    <span className={item.name} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CharacterEmoticon;
